import React, { useContext, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Circle, useMap } from 'react-leaflet';
import { LatLngBoundsLiteral, LatLngExpression, icon, latLng } from 'leaflet';
import MapMarkerWrapper from './MapMarkerWrapper';
import types from '../../types/services-api';
import { Helmet } from 'react-helmet';
import {
  JobPostingCSVJobType,
  JobPostingCSVPartnerType,
} from '../../types/csv';
import { isCsvJob, isPartnerAuthJob } from '../../context/JobSearchContext';
import { UserContext, UserContextType } from '../../context/UserContext';
import { GoogleAddressType } from './AddressAutocomplete';
import { graphql, useStaticQuery } from 'gatsby';
import LoadingSpinner from './LoadingSpinner';

export type JobType =
  | types.JobViewType
  | types.JobInternalViewType
  | JobPostingCSVJobType
  | JobPostingCSVPartnerType
  | types.JobOwnerDetailViewType
  | types.PartnerJobDetailViewType
  | types.JobApplicationViewType
  | ((GoogleAddressType & { lat: number; lng: number }) & {
      isConfidential: boolean;
    });

const MapWrapper = (props: {
  jobs: JobType[];
  activeCategory?: 'NATIONAL' | 'EXTERNAL' | 'SEARCH';
}) => {
  const data = useStaticQuery(iconQuery);
  const { greenMarker, blueMarker } = data;
  const { jobs } = props;
  const { defaultMapCenter } = useContext(UserContext) as UserContextType;
  const [center, setCenter] = useState<LatLngExpression | undefined>(undefined);
  const [zoom, setZoom] = useState<4 | 10>(10);

  useEffect(() => {
    if (props.activeCategory === 'NATIONAL') {
      setZoom(4);
    } else {
      setZoom(10);
    }
  }, [props.activeCategory]);

  useEffect(() => {
    if (jobs.length > 0) {
      const newCenter: LatLngExpression = [
        Number(jobs[0].lat),
        Number(jobs[0].lng),
      ];
      setCenter(newCenter);
    } else if (jobs.length === 0 && !!defaultMapCenter)
      setCenter(defaultMapCenter);
  }, [jobs]);

  const bounds: LatLngBoundsLiteral | undefined =
    !!jobs && jobs.length > 0
      ? jobs.map((job) => {
          const lat = Number(job.lat);
          const lng = Number(job.lng);
          return [lat, lng];
        })
      : undefined;

  const maxZoom =
    jobs?.length === 1 &&
    !isPartnerAuthJob(jobs[0]) &&
    ('isConfidential' in jobs[0] ? jobs[0].isConfidential : false)
      ? 12
      : 14;

  function ResetMapCenter() {
    const map = useMap();
    if (!!center) {
      map.panTo(center);
    }
    return null;
  }

  function ResetMapZoom() {
    const map = useMap();
    if (zoom === 10 && props.activeCategory === 'NATIONAL') {
      map.zoomOut(6);
    } else if (zoom === 4 && props.activeCategory !== 'NATIONAL') {
      map.zoomIn(6);
    }
    return null;
  }

  return (
    <div className="h-100 w-100 bg-light">
      <Helmet>
        <link rel="stylesheet" href="/styles/leaflet.css" />
      </Helmet>
      {!!center && !!jobs ? (
        <MapContainer
          bounds={bounds}
          center={center}
          zoom={zoom}
          maxZoom={maxZoom}
          attributionControl={false}
          style={{ height: '100%' }}
        >
          <TileLayer url="https://api.maptiler.com/maps/basic-v2/256/{z}/{x}/{y}.png?key=90vTrAsedbmk6OtJ8S1a" />
          {jobs.length === 1 && (
            <>
              {'isConfidential' in jobs[0] &&
              (isCsvJob(jobs[0])
                ? jobs[0].isConfidential === 'true'
                : jobs[0].isConfidential) ? (
                <Circle
                  center={latLng(Number(jobs[0].lat), Number(jobs[0].lng))}
                  radius={5000}
                />
              ) : (
                <MapMarkerWrapper
                  job={jobs[0]}
                  icon={
                    isPartnerAuthJob(jobs[0])
                      ? icon({
                          iconUrl: blueMarker.publicURL,
                          iconSize: [25, 40],
                        })
                      : icon({
                          iconUrl: greenMarker.publicURL,
                          iconSize: [25, 40],
                          iconAnchor: [12, 40],
                        })
                  }
                />
              )}
            </>
          )}

          {jobs.length > 1 &&
            jobs.map((job, index) => {
              return (
                <MapMarkerWrapper
                  key={`marker-${jobs[0].city}-${index}`}
                  job={job}
                  icon={
                    'id' in job
                      ? icon({
                          iconUrl: greenMarker.publicURL,
                          iconSize: [25, 40],
                          iconAnchor: [12, 40],
                        })
                      : icon({
                          iconUrl: blueMarker.publicURL,
                          iconSize: [25, 40],
                        })
                  }
                />
              );
            })}
          <ResetMapCenter />
          <ResetMapZoom />
        </MapContainer>
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};

export default MapWrapper;

export const iconQuery = graphql`
  query {
    blueMarker: file(relativePath: { eq: "marker-icon-blue.png" }) {
      publicURL
    }
    greenMarker: file(relativePath: { eq: "marker-icon-green.png" }) {
      publicURL
    }
  }
`;
